import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { AppLogo } from '../components/AppLogo';
import { Seo } from '../components/marketing/Seo';

/**
 * Data aktualizacji jest **per dokument**, nie wspólna dla całej powłoki.
 *
 * Jedna stała podbijała ją także na Regulaminie, którego treści nikt nie
 * ruszał — nieprawda w dokumencie prawnym, a przy okazji zepsuty jedyny
 * sygnał, po którym klient poznaje, że warto przeczytać dokument ponownie.
 * Zmieniając treść dokumentu, zmień TĘ JEDNĄ datę, która go dotyczy.
 */
const PRIVACY_UPDATED_AT = '31 sierpnia 2026';
const TERMS_UPDATED_AT = '7 czerwca 2026';
const DELETE_ACCOUNT_UPDATED_AT = '7 czerwca 2026';

function LegalShell({
  title,
  subtitle,
  updatedAt,
  children,
}: {
  title: string;
  subtitle: string;
  updatedAt: string;
  children: ReactNode;
}) {
  return (
    <div className="min-h-screen bg-paper text-ink">
      <header className="border-b border-line bg-card/60 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-5 sm:px-8">
          <Link to="/" className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-950 text-white">
              <AppLogo className="h-5 w-5" />
            </span>
            <span>
              <span className="block text-sm font-bold uppercase tracking-[0.24em]">
                Pinveo
              </span>
              <span className="block text-xs text-muted">
                dokumenty projektu
              </span>
            </span>
          </Link>
          <Link
            to="/"
            className="rounded-full border border-line bg-card/70 px-4 py-2 text-sm font-semibold text-ink-soft transition hover:bg-card"
          >
            Strona główna
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 py-10 sm:px-8 sm:py-14">
        <div className="mb-8 rounded-[2rem] border border-line/70 bg-card/70 p-7 shadow-sm backdrop-blur">
          <p className="text-sm font-bold uppercase tracking-[0.22em] text-hi">
            Ostatnia aktualizacja: {updatedAt}
          </p>
          <h1 className="mt-3 text-4xl font-black tracking-[-0.04em] sm:text-5xl">
            {title}
          </h1>
          <p className="mt-4 max-w-3xl text-base leading-8 text-ink-soft">
            {subtitle}
          </p>
        </div>

        <article className="prose prose-slate dark:prose-invert max-w-none rounded-[2rem] border border-line/70 bg-card/75 p-7 shadow-sm backdrop-blur prose-headings:tracking-tight prose-a:font-semibold prose-a:text-ink sm:p-10">
          {children}
        </article>
      </main>
    </div>
  );
}

export function PrivacyPolicyPage() {
  return (
    <LegalShell
      updatedAt={PRIVACY_UPDATED_AT}
      title="Polityka prywatności i RODO"
      subtitle="Informacja o przetwarzaniu danych osobowych w aplikacji Pinveo — role administratora i podmiotu przetwarzającego, sub-procesory, przechowywanie plików, AI, prawa osób."
    >
      <Seo
        title="Polityka prywatności"
        description="Polityka prywatności i informacja o przetwarzaniu danych osobowych w aplikacji Pinveo — zgodność z RODO."
        path="/privacy"
      />

      <h2>1. Administrator danych i kontakt</h2>
      <p>
        Administratorem danych osobowych przetwarzanych w ramach aplikacji
        Pinveo jest <strong>Seweryn Rogut</strong>, osoba prywatna rozwijająca
        aplikację jako narzędzie do dokumentacji terenowej i inspekcji obiektów.
      </p>
      <p>
        W sprawach dotyczących ochrony danych osobowych prosimy o kontakt na
        adres e-mail:{' '}
        <a href="mailto:support@pinveo.app">support@pinveo.app</a>.
        Kontakt możliwy jest również przez formularz{' '}
        <Link to="/request-access">Poproś o dostęp</Link>.
      </p>

      <h2>2. Role: administrator i podmiot przetwarzający</h2>
      <p>
        Pinveo pełni dwie różne role w zależności od kategorii danych:
      </p>
      <ul>
        <li>
          <strong>Administrator danych</strong> — dla danych konta użytkownika,
          danych dostępowych, logów bezpieczeństwa i danych analitycznych
          (po wyrażeniu zgody). W tym zakresie Pinveo samodzielnie decyduje
          o celach i środkach przetwarzania.
        </li>
        <li>
          <strong>Podmiot przetwarzający (procesor)</strong> — dla danych osób
          trzecich wprowadzanych do aplikacji przez organizacje (np. dane
          pracowników, klientów, dane z dokumentacji terenowej). W tym zakresie
          administratorem jest organizacja korzystająca z aplikacji, a Pinveo
          przetwarza dane wyłącznie na jej zlecenie i zgodnie z jej instrukcjami.
        </li>
      </ul>
      <p>
        Warunki powierzenia przetwarzania danych osobowych, w tym elementy
        wymagane przez art. 28 ust. 3 RODO, zawarte są w sekcji „Powierzenie
        przetwarzania danych osobowych" w{' '}
        <Link to="/terms">Regulaminie korzystania z aplikacji</Link>.
      </p>

      <h2>3. Jakie dane mogą być przetwarzane</h2>
      <ul>
        <li>
          <strong>Dane z formularza prośby o dostęp:</strong> imię, nazwisko,
          adres e-mail, telefon, nazwa firmy, NIP oraz treść wiadomości.
        </li>
        <li>
          <strong>Dane konta użytkownika:</strong> adres e-mail, imię, nazwisko,
          rola, przynależność do organizacji, historia logowania.
        </li>
        <li>
          <strong>Dane wprowadzane w aplikacji:</strong> projekty, obiekty,
          markery, notatki, formularze, dokumenty,{' '}
          <strong>zdjęcia i inne pliki</strong>, a także{' '}
          <strong>dane lokalizacyjne (GPS)</strong> powiązane ze wpisami
          terenowymi.
        </li>
        <li>
          <strong>Dane techniczne:</strong> adres IP, identyfikatory sesji,
          logi bezpieczeństwa, informacje o błędach i zdarzeniach systemowych.
        </li>
      </ul>

      <h2>4. Cele i podstawy przetwarzania</h2>
      <ul>
        <li>
          obsługa prośby o dostęp i kontakt zwrotny — art. 6 ust. 1 lit. b
          RODO (działania przed udzieleniem dostępu) oraz art. 6 ust. 1 lit. f
          RODO (uzasadniony interes administratora);
        </li>
        <li>
          prowadzenie kont użytkowników i zabezpieczenie aplikacji — art. 6
          ust. 1 lit. b RODO (wykonanie uzgodnionego dostępu) oraz art. 6
          ust. 1 lit. f RODO (uzasadniony interes);
        </li>
        <li>
          zapewnienie działania funkcji aplikacji, synchronizacji offline,
          raportów i audytu — art. 6 ust. 1 lit. b RODO;
        </li>
        <li>
          ochrona przed nadużyciami, spamem i nieuprawnionym dostępem — art. 6
          ust. 1 lit. f RODO (uzasadniony interes);
        </li>
        <li>
          analityka ruchu (Google Analytics) — art. 6 ust. 1 lit. a RODO
          (zgoda), wyłącznie po jej wyrażeniu;
        </li>
        <li>
          realizacja obowiązków wynikających z prawa, jeśli takie obowiązki
          powstaną — art. 6 ust. 1 lit. c RODO.
        </li>
      </ul>

      <h2>5. Odbiorcy danych, sub-procesory i lokalizacja</h2>
      <p>
        Dane mogą być przekazywane następującym kategoriom odbiorców
        (sub-procesorów), działającym wyłącznie w zakresie niezbędnym do
        świadczenia usługi:
      </p>
      <ul>
        <li>
          <strong>Dostawca infrastruktury serwerowej</strong> — hosting
          aplikacji backendowej; zlokalizowany w Europejskim Obszarze
          Gospodarczym (EOG).
        </li>
        <li>
          <strong>Magazyn plików zgodny z S3</strong> — przechowywanie zdjęć,
          dokumentów i innych plików wgrywanych przez użytkowników;
          zlokalizowany w EOG.
        </li>
        <li>
          <strong>Dostawca usług poczty elektronicznej</strong> —
          wysyłka powiadomień i wiadomości transakcyjnych; zlokalizowany w EOG.
        </li>
        <li>
          <strong>Monitoring błędów</strong> — własna infrastruktura
          monitoringu (GlitchTip); zlokalizowana w EOG.
        </li>
        <li>
          <strong>Google Ireland Ltd.</strong> (Gordon House, Barrow Street,
          Dublin 4, Irlandia) — Google Analytics 4; wyłącznie po wyrażeniu
          zgody przez użytkownika. Dane mogą być przetwarzane poza EOG na
          podstawie standardowych klauzul umownych (SCC); adres IP jest
          anonimizowany przed przesłaniem do Google.
        </li>
        <li>
          <strong>Dostawca modelu sztucznej inteligencji</strong> (za
          pośrednictwem OpenRouter) — analiza zdjęć uszkodzeń w opcjonalnym
          module „Analiza AI"; wyłącznie dla organizacji, dla których moduł
          został włączony na ich wniosek przez administratora platformy Pinveo
          (domyślnie wyłączony — administrator organizacji nie ma dostępu do
          tego przełącznika i nie włącza go samodzielnie). Do dostawcy trafiają
          zdjęcia dołączone do markera poddanego analizie (nie panoramy),
          automatycznie ograniczone do kilku najstarszych wg konfiguracji —
          nie cała dokumentacja projektu. Dane mogą być przetwarzane poza
          Europejskim Obszarem Gospodarczym; więcej w sekcji 8.
        </li>
      </ul>
      <p>
        Poza Google Analytics i modułem „Analiza AI" (gdy włączony) dane nie
        są przekazywane poza Europejski Obszar Gospodarczy.
      </p>

      <h2>6. Przechowywanie plików i bezpieczeństwo</h2>
      <p>
        Zdjęcia, dokumenty i inne pliki wgrywane przez użytkowników są
        przechowywane w magazynie plików zgodnym z S3, zlokalizowanym
        w Europejskim Obszarze Gospodarczym. Stosowane środki bezpieczeństwa
        obejmują:
      </p>
      <ul>
        <li>
          dostęp do plików wyłącznie przez czasowe linki podpisywane
          kryptograficznie (pre-signed URLs) — bezpośredni publiczny dostęp
          do pliku jest niemożliwy;
        </li>
        <li>
          kontrolę dostępu per rola i per organizacja — użytkownik może
          uzyskać dostęp wyłącznie do plików w ramach organizacji, do której
          należy;
        </li>
        <li>
          szyfrowanie połączeń w transmisji (TLS);
        </li>
        <li>
          kopie zapasowe wykonywane zgodnie z harmonogramem retencji.
        </li>
      </ul>

      <h2>7. Okres przechowywania</h2>
      <ul>
        <li>
          <strong>Dane z prośby o dostęp</strong> — przez czas potrzebny do
          obsługi zgłoszenia, po czym usuwane lub przekształcane w konto
          użytkownika.
        </li>
        <li>
          <strong>Dane konta i treści projektów</strong> — przez okres
          utrzymywania dostępu, do usunięcia konta lub projektu albo zakończenia
          współpracy.
        </li>
        <li>
          <strong>Logi bezpieczeństwa i ślady audytowe</strong> — przez zakres
          wymagany przepisami prawa lub niezbędny do ochrony przed nadużyciami.
        </li>
        <li>
          <strong>Kopie zapasowe</strong> — usuwane zgodnie z harmonogramem
          retencji kopii zapasowych.
        </li>
      </ul>

      <h2>8. Sztuczna inteligencja i zautomatyzowane decyzje</h2>
      <p>
        Dane wprowadzane w aplikacji Pinveo nie są wykorzystywane do trenowania
        modeli sztucznej inteligencji. Nie stosujemy zautomatyzowanego
        podejmowania decyzji ani profilowania wywołującego wobec użytkownika
        skutki prawne lub podobnie istotne w rozumieniu art. 22 RODO.
      </p>
      <p>
        Opcjonalny moduł „Analiza AI" jest domyślnie wyłączony. Włącza go, na
        wniosek organizacji, administrator platformy Pinveo — to nie jest
        samoobsługowy przełącznik dostępny w panelu administratora
        organizacji. Po włączeniu, zdjęcia dołączone do markera poddanego
        analizie (nie panoramy) są przesyłane do zewnętrznego dostawcy modelu
        językowego za pośrednictwem OpenRouter, w celu wygenerowania propozycji
        opisu i typu uszkodzenia wg zamkniętej taksonomii. Zdjęcia dobiera
        automatycznie system — kilka najstarszych wg konfiguracji, nigdy cała
        dokumentacja projektu. Zasady retencji i wykorzystania danych
        przekazanych temu dostawcy wynikają z jego regulaminu — nie
        dysponujemy technicznym ani umownym mechanizmem, który by nam to
        gwarantował. Propozycja wygenerowana przez model nigdy nie trafia
        automatycznie do dokumentacji projektu — wymaga świadomej decyzji
        osoby z odpowiednimi uprawnieniami, która może ją przyjąć bez zmian,
        poprawić przed zapisem albo odrzucić. Dopiero zaakceptowana treść
        staje się notatką w projekcie, widocznie oznaczoną jako pochodząca
        z analizy AI.
      </p>

      <h2>9. Prawa osoby, której dane dotyczą</h2>
      <p>
        Osobie, której dane dotyczą, przysługują następujące prawa: dostępu do
        danych, sprostowania, usunięcia, ograniczenia przetwarzania,
        przenoszenia danych, sprzeciwu wobec przetwarzania oraz wycofania zgody
        (jeśli przetwarzanie odbywa się na podstawie zgody — wycofanie nie
        wpływa na zgodność z prawem przetwarzania przed wycofaniem).
        Przysługuje również prawo wniesienia skargi do Prezesa Urzędu Ochrony
        Danych Osobowych (ul. Stawki 2, 00-193 Warszawa, <a href="https://uodo.gov.pl" target="_blank" rel="noreferrer">uodo.gov.pl</a>).
      </p>
      <p>
        W zakresie danych osobowych powierzonych przez organizację (dane osób
        trzecich w projektach) prawa przysługujące osobom, których dane dotyczą,
        realizowane są przez organizację pełniącą rolę administratora tych
        danych. Pinveo wspiera organizację w wykonaniu tych praw.
      </p>

      <h2>10. Usunięcie konta i danych</h2>
      <p>
        Procedura usunięcia konta oraz powiązanych z nim danych opisana jest na
        stronie <Link to="/delete-account">Usunięcie konta</Link>. Zgłoszenie
        jest weryfikowane przez administratora i realizowane w ciągu 30 dni od
        potwierdzenia tożsamości. Zachowywane mogą być wyłącznie logi
        bezpieczeństwa i ślady audytowe w zakresie wymaganym prawem lub do
        ochrony przed nadużyciami.
      </p>

      <h2>11. Dobrowolność podania danych</h2>
      <p>
        Podanie danych w formularzu prośby o dostęp jest dobrowolne, ale brak
        danych kontaktowych uniemożliwia obsługę zgłoszenia. Podanie danych
        w aplikacji zależy od rodzaju prowadzonej dokumentacji i zakresu
        udzielonego dostępu.
      </p>

      <h2>12. Pliki cookies i dane techniczne</h2>
      <p>
        Aplikacja wykorzystuje dwa rodzaje plików cookie i mechanizmów
        przechowywania danych w przeglądarce:
      </p>
      <ul>
        <li>
          <strong>Niezbędne</strong> — pliki cookie i dane
          sessionStorage/localStorage wymagane do logowania, utrzymania sesji,
          bezpieczeństwa, pracy offline oraz zapamiętania ustawień aplikacji.
          Są aktywne zawsze, bez konieczności wyrażenia zgody (podstawa:
          uzasadniony interes administratora / niezbędność do wykonania usługi).
        </li>
        <li>
          <strong>Analityczne — Google Analytics 4</strong> (dostawca: Google
          Ireland Ltd., Gordon House, Barrow Street, Dublin 4, Irlandia) —
          służą do zbierania anonimowych danych o ruchu w serwisie (odwiedzane
          strony, czas wizyty, typ urządzenia) w celu ulepszania aplikacji
          Pinveo. Analityczne pliki cookie instalowane są{' '}
          <strong>wyłącznie po wyrażeniu zgody</strong> w banerze cookie.
          Podstawą przetwarzania jest zgoda (art. 6 ust. 1 lit. a RODO), którą
          możesz w każdej chwili wycofać. Adres IP jest anonimizowany przed
          przesłaniem do Google. Dane mogą być przetwarzane przez Google poza
          Europejskim Obszarem Gospodarczym na podstawie standardowych klauzul
          umownych. Zgodę zmienisz lub wycofasz, czyszcząc pliki cookie
          przeglądarki albo wybierając „Odrzucam" w banerze cookie widocznym
          przy pierwszej wizycie.
        </li>
      </ul>

      <h2>13. Zmiany polityki prywatności</h2>
      <p>
        Niniejsza polityka może być aktualizowana wraz z rozwojem aplikacji
        i zmieniającymi się wymaganiami prawnymi. Data ostatniej aktualizacji
        widoczna jest na górze strony. O istotnych zmianach informujemy
        użytkowników posiadających aktywny dostęp do aplikacji.
      </p>
    </LegalShell>
  );
}

export function TermsPage() {
  return (
    <LegalShell
      updatedAt={TERMS_UPDATED_AT}
      title="Regulamin korzystania z aplikacji"
      subtitle="Zasady dostępu do aplikacji Pinveo, odpowiedzialności, dostępności usługi oraz powierzenia przetwarzania danych osobowych (art. 28 RODO)."
    >
      <Seo
        title="Regulamin"
        description="Regulamin korzystania z aplikacji Pinveo — zasady dostępu, kont, danych i odpowiedzialności."
        path="/terms"
      />

      <h2>1. Informacje ogólne</h2>
      <p>
        Aplikację Pinveo rozwija <strong>Seweryn Rogut</strong> jako narzędzie
        do dokumentacji terenowej, inspekcji obiektów, pracy na rzutach
        i generowania raportów.
      </p>
      <p>
        Dostęp do aplikacji jest zamknięty i wymaga zaakceptowania prośby o
        dostęp przez administratora. Samo wysłanie formularza nie gwarantuje
        utworzenia konta.
      </p>

      <h2>2. Status aplikacji</h2>
      <p>
        Pinveo znajduje się w fazie aktywnego rozwoju. Poszczególne funkcje
        mogą być zmieniane, wyłączane, przebudowywane lub udostępniane tylko
        wybranym użytkownikom i organizacjom.
      </p>

      <h2>3. Konto i bezpieczeństwo</h2>
      <ul>
        <li>Użytkownik powinien podawać prawdziwe dane kontaktowe.</li>
        <li>Dane logowania nie powinny być przekazywane innym osobom.</li>
        <li>
          Administrator może zablokować dostęp w przypadku nadużycia,
          naruszenia bezpieczeństwa lub użycia aplikacji niezgodnie
          z przeznaczeniem.
        </li>
      </ul>

      <h2>4. Odpowiedzialność i jej ograniczenie</h2>
      <p>
        Aplikacja Pinveo udostępniana jest w stanie, w jakim się znajduje
        („as-is"), w miarę dostępności, bez gwarancji przydatności do
        określonego celu. Odpowiedzialność Pinveo jest ograniczona w zakresie
        dozwolonym przez bezwzględnie obowiązujące przepisy prawa, w tym
        przepisy ochrony konsumentów, które mają zastosowanie.
      </p>
      <p>
        Aplikacja wspiera dokumentowanie pracy terenowej, lecz nie zastępuje
        wiedzy zawodowej, wymaganych uprawnień ani formalnej oceny technicznej.
        Użytkownik odpowiada za poprawność danych, zdjęć, formularzy
        i raportów, które wprowadza lub przekazuje dalej.
      </p>
      <p>
        Użytkownik powinien we własnym zakresie wykonywać kopie danych
        krytycznych — Pinveo dokłada starań w zakresie kopii zapasowych, lecz
        nie ponosi odpowiedzialności za utratę danych wynikającą ze zdarzeń
        od nas niezależnych (siła wyższa, awarie infrastruktury zewnętrznej,
        błędy użytkownika).
      </p>

      <h2>5. Dostępność usługi</h2>
      <p>
        Dążymy do zapewnienia jak najwyższej ciągłości działania aplikacji,
        jednak możliwe są planowane i nieplanowane przerwy w dostępności —
        wynikające z prac konserwacyjnych, awarii infrastruktury lub czynników
        zewnętrznych pozostających poza naszą kontrolą.
      </p>
      <p>
        Nie gwarantujemy 100% dostępności ani określonego poziomu SLA.
        Nie ponosimy odpowiedzialności za przerwy wynikające z okoliczności
        wykraczających poza zakres rozsądnych starań w zakresie utrzymania usługi.
      </p>

      <h2>6. Dane i materiały użytkownika</h2>
      <p>
        Użytkownik powinien wprowadzać tylko takie dane, zdjęcia i dokumenty,
        do których ma prawo i które może legalnie przetwarzać w ramach swojej
        pracy. Szczególną ostrożność należy zachować przy zdjęciach osób,
        wnętrz prywatnych, danych klientów, dokumentach technicznych i danych
        lokalizacyjnych.
      </p>
      <p>
        Organizacja korzystająca z aplikacji oświadcza, że posiada odpowiednie
        uprawnienia do wprowadzania danych, w tym danych osobowych osób trzecich,
        oraz że przetwarza je zgodnie z obowiązującymi przepisami prawa, w
        szczególności z RODO.
      </p>
      <p>
        Serwis korzysta z plików cookie i Google Analytics zgodnie z{' '}
        <Link to="/privacy">Polityką prywatności</Link>; analityka działa
        wyłącznie po wyrażeniu zgody przez użytkownika.
      </p>

      <h2>7. Powierzenie przetwarzania danych osobowych (art. 28 RODO)</h2>
      <p>
        W zakresie, w jakim organizacja (użytkownik) wprowadza do aplikacji
        dane osobowe osób trzecich (np. pracowników, klientów, osób wizytowanych
        obiektów), organizacja pełni rolę <strong>administratora</strong> tych
        danych, a Pinveo — rolę{' '}
        <strong>podmiotu przetwarzającego (procesora)</strong> działającego na
        jej zlecenie. Niniejszy regulamin stanowi umowę powierzenia
        przetwarzania w rozumieniu art. 28 RODO. Warunki powierzenia:
      </p>
      <ul>
        <li>
          <strong>Przedmiot, czas, charakter i cel przetwarzania:</strong>{' '}
          świadczenie funkcji aplikacji Pinveo (dokumentacja terenowa, inspekcje,
          raporty) na rzecz organizacji przez okres korzystania przez nią
          z aplikacji.
        </li>
        <li>
          <strong>Rodzaje danych i kategorie osób:</strong> dane kontaktowe
          i identyfikacyjne, wizerunek na zdjęciach, dane w formularzach
          i dokumentach, dane lokalizacyjne — dotyczące osób wskazanych przez
          organizację (np. pracownicy, klienci, osoby wizytowanych obiektów).
        </li>
        <li>
          <strong>Przetwarzanie wyłącznie na udokumentowane instrukcje:</strong>{' '}
          Pinveo przetwarza dane osobowe wyłącznie zgodnie z udokumentowanymi
          instrukcjami administratora (organizacji); niniejszy regulamin oraz
          dokumentacja funkcji aplikacji stanowią takie instrukcje. Pinveo
          niezwłocznie informuje administratora, jeśli uzna, że instrukcja
          narusza przepisy RODO lub inne przepisy o ochronie danych.
        </li>
        <li>
          <strong>Poufność osób upoważnionych:</strong> osoby upoważnione
          do przetwarzania danych w imieniu Pinveo zobowiązane są do zachowania
          poufności lub podlegają stosownemu obowiązkowi prawnemu.
        </li>
        <li>
          <strong>Środki bezpieczeństwa (art. 32 RODO):</strong> Pinveo stosuje
          odpowiednie techniczne i organizacyjne środki bezpieczeństwa, w tym
          szyfrowanie połączeń (TLS), kontrolę dostępu per rola i organizacja,
          dostęp do plików przez podpisywane czasowe linki oraz kopie zapasowe.
        </li>
        <li>
          <strong>Korzystanie z sub-procesorów:</strong> Pinveo korzysta
          z sub-procesorów wymienionych w Polityce prywatności (sekcja 5),
          zlokalizowanych w EOG. Organizacja wyraża ogólną zgodę na korzystanie
          z sub-procesorów, przy czym przysługuje jej prawo wniesienia sprzeciwu
          wobec zmian w liście sub-procesorów. Pinveo zobowiązuje sub-procesorów
          do stosowania równoważnych wymogów ochrony danych.
        </li>
        <li>
          <strong>Pomoc administratorowi w realizacji praw osób
          i obowiązków (art. 32–36 RODO):</strong> Pinveo, uwzględniając
          charakter przetwarzania, pomaga administratorowi wywiązywać się
          z obowiązku odpowiadania na żądania osób, których dane dotyczą,
          oraz z obowiązków wynikających z art. 32–36 RODO (bezpieczeństwo,
          ocena skutków, uprzednie konsultacje).
        </li>
        <li>
          <strong>Zgłaszanie naruszeń ochrony danych:</strong> Pinveo zgłasza
          administratorowi wszelkie naruszenia ochrony danych osobowych bez
          zbędnej zwłoki po ich stwierdzeniu.
        </li>
        <li>
          <strong>Usunięcie lub zwrot danych po zakończeniu:</strong> po
          zakończeniu korzystania z usług Pinveo — na żądanie administratora —
          usuwa lub zwraca wszelkie dane osobowe i usuwa istniejące ich kopie,
          chyba że przepisy prawa wymagają ich zachowania.
        </li>
        <li>
          <strong>Umożliwienie audytów i inspekcji:</strong> Pinveo udostępnia
          administratorowi informacje niezbędne do wykazania zgodności
          z art. 28 RODO oraz umożliwia przeprowadzanie audytów lub inspekcji
          — bezpośrednio lub przez upoważnionego audytora.
        </li>
      </ul>

      <h2>8. Dostęp testowy i early access</h2>
      <p>
        W przypadku dostępu testowego aplikacja może być udostępniana bez
        gwarancji ciągłości, kompletności funkcji lub gotowości produkcyjnej.
        Funkcje eksperymentalne mogą wymagać dodatkowych ustaleń z
        administratorem projektu.
      </p>

      <h2>9. Własność intelektualna</h2>
      <p>
        Kod, projekt interfejsu, nazwa Pinveo w kontekście tej aplikacji oraz
        materiały opisujące aplikację stanowią elementy projektu rozwijanego
        przez autora, o ile nie wskazano inaczej. Użytkownik zachowuje prawa do
        materiałów, które sam wprowadza do aplikacji.
      </p>

      <h2>10. Zmiany regulaminu</h2>
      <p>
        Regulamin może być aktualizowany wraz z rozwojem aplikacji. Istotne
        zmiany powinny być komunikowane użytkownikom mającym aktywny dostęp.
        Data ostatniej aktualizacji widoczna jest na górze strony.
      </p>

      <h2>11. Kontakt i zgłoszenia</h2>
      <p>
        Pytania dotyczące dostępu, działania aplikacji, danych lub zasad
        korzystania można przekazać na adres e-mail:{' '}
        <a href="mailto:support@pinveo.app">support@pinveo.app</a> albo
        przez formularz{' '}
        <Link to="/request-access">Poproś o dostęp</Link>.
      </p>
    </LegalShell>
  );
}

/**
 * v0.61.3 — strona "Usunięcie konta", wymagana przez Google Play Data Safety
 * dla aplikacji pozwalających tworzyć konto (Inspector Mobile linkuje tutaj
 * przez `ExternalLinks.deleteAccount`).
 *
 * Wersja MVP: instrukcja + mailto link. Pełen formularz / auto-procesowanie
 * w późniejszym sprincie (Sprint Account-Deletion) — Google wymaga tylko
 * **dostępu do instrukcji** w aplikacji + na publicznym URL.
 *
 * Patrz `inspector_mobile/docs/GOOGLE_PLAY_RELEASE_AUDIT.md` §3.2.
 */
export function DeleteAccountPage() {
  // Publiczny adres support@pinveo.app (forwardowany na realny inbox) — wystawiamy
  // alias zamiast realnej skrzynki, by: (1) łatwo zmienić routing bez deploya PWA,
  // (2) realny adres nie wyciekał do Play Store listing / bot scraperów.
  // Seo noindex — strona instruktażowa, nie powinna być indeksowana.
  const contactEmail = 'support@pinveo.app';
  const subject = encodeURIComponent('Pinveo — prośba o usunięcie konta');
  const body = encodeURIComponent(
    'Dzień dobry,\n\n' +
      'Proszę o usunięcie mojego konta w aplikacji Pinveo.\n\n' +
      'Email konta: \n' +
      'Imię i nazwisko: \n' +
      'Powód (opcjonalnie): \n\n' +
      'Potwierdzam, że jestem właścicielem tego konta i rozumiem, że dane ' +
      'zostaną usunięte bezpowrotnie w ciągu 30 dni od potwierdzenia tożsamości.\n\n' +
      'Z poważaniem,\n',
  );
  const mailtoUrl = `mailto:${contactEmail}?subject=${subject}&body=${body}`;

  return (
    <LegalShell
      updatedAt={DELETE_ACCOUNT_UPDATED_AT}
      title="Usunięcie konta i danych"
      subtitle="Procedura zamknięcia konta w aplikacji Pinveo i usunięcia powiązanych danych — dla użytkowników web oraz aplikacji mobilnej."
    >
      <Seo
        title="Usunięcie konta"
        description="Procedura usunięcia konta i danych osobowych w aplikacji Pinveo — instrukcja dla użytkowników web i mobilnych."
        path="/delete-account"
        noindex
      />
      <h2>Co się stanie po wysłaniu prośby</h2>
      <ol>
        <li>
          Wysyłasz wiadomość email na adres podany niżej (gotowy szablon
          poniżej).
        </li>
        <li>
          Administrator weryfikuje, że jesteś właścicielem konta — odpisuje na
          adres z którego wysłałeś wiadomość. Może poprosić o dodatkowe
          informacje (data ostatniego logowania, nazwa organizacji), żeby
          uniknąć usunięcia konta przez osobę trzecią.
        </li>
        <li>
          Po potwierdzeniu tożsamości konto i dane osobowe powiązane z kontem
          (imię, nazwisko, email, telefon, historia logowania) są usuwane w
          ciągu 30 dni roboczych.
        </li>
        <li>
          Otrzymujesz email z finalnym potwierdzeniem usunięcia.
        </li>
      </ol>

      <h2>Co jest usuwane</h2>
      <ul>
        <li>dane konta (email, imię, nazwisko, telefon, hasło, biometria local-only),</li>
        <li>powiązanie z organizacją i projektami,</li>
        <li>preferencje, ustawienia notyfikacji, sesje,</li>
        <li>logi logowania powyżej okresu retencji wymaganego prawem.</li>
      </ul>

      <h2>Co może zostać zachowane</h2>
      <p>
        Niektóre dane mogą być zachowane w zakresie wymaganym prawem lub do
        ochrony przed nadużyciami:
      </p>
      <ul>
        <li>
          treści dokumentacji terenowej (markery, zdjęcia, notatki, formularze)
          które zostały utworzone w projektach należących do organizacji —
          stanowią one element pracy organizacji, nie konta. Po usunięciu konta
          treści są anonimizowane (autor jest oznaczany jako "usunięty użytkownik")
          ale pozostają w projekcie. Jeśli wymagasz usunięcia również treści,
          zaznacz to wyraźnie w prośbie — admin organizacji musi to potwierdzić.
        </li>
        <li>logi audytowe i bezpieczeństwa wymagane prawem,</li>
        <li>kopie zapasowe — usuwane zgodnie z harmonogramem retencji backupów.</li>
      </ul>

      <h2>Jak wysłać prośbę</h2>
      <p>
        Najprościej kliknij poniższy przycisk — otworzy Twojego klienta poczty
        z gotowym szablonem. Wystarczy uzupełnić email konta i wysłać:
      </p>
      <p>
        <a
          href={mailtoUrl}
          className="not-prose inline-flex items-center gap-2 rounded-2xl border border-cyan-700 bg-cyan-700 px-6 py-3 font-semibold text-white shadow transition hover:bg-cyan-800"
        >
          Napisz email z szablonem
        </a>
      </p>
      <p>
        Jeśli przycisk nie działa, skopiuj poniższe dane do swojej poczty:
      </p>
      <ul>
        <li>
          <strong>Adres:</strong>{' '}
          <a href={`mailto:${contactEmail}`}>{contactEmail}</a>
        </li>
        <li>
          <strong>Temat:</strong> Pinveo — prośba o usunięcie konta
        </li>
        <li>
          <strong>W treści podaj:</strong> email konta, imię i nazwisko,
          opcjonalnie powód.
        </li>
      </ul>

      <h2>Kontakt w innych sprawach</h2>
      <p>
        Jeśli chcesz tylko zmienić email/hasło lub wyłączyć notyfikacje, nie
        usuwaj konta — wszystkie te ustawienia są dostępne w aplikacji (web w
        Profilu, mobile w Profilu i Ustawieniach). Tematy dotyczące dostępu i
        konfiguracji można też zgłosić przez formularz{' '}
        <Link to="/request-access">Poproś o dostęp</Link>.
      </p>
    </LegalShell>
  );
}
